/**
 * Aggregated data for a single reference
 */
export type ReferenceAggregation = {
  reference: string;
  // Summed metrics
  totalMachiningDuration: number;
  pieceMachiningDuration: number;
  machinedVolume: number;
  machinedDistance: number;
  machinedPieces: number;
  // Mean metrics
  malround: number;
  // Additional info
  toolCount: number;
  traceCount: number;
  // Optional: Keep first occurrence of categorical fields for context
  machine?: string;
  material?: string;
  lubType?: string;
  holderType?: string;
};

import type { Tool } from '@/features/traceability/type/Tool';

/**
 * Aggregates trace data by tool reference
 *
 * This function groups all traces by their tool's reference and aggregates metrics:
 * - SUM: totalMachiningDuration, pieceMachiningDuration, machinedVolume, machinedDistance, machinedPieces
 * - MEAN: malround (only non-null values)
 * - COUNT: number of tools and traces per reference
 *
 * @param traces - Array of traces with toolId and reference
 * @param tools - Array of tools to get reference information
 * @returns Array of aggregated reference data
 */
export function aggregateTracesByReference(
  traces: (Trace & { toolId: string; reference?: string })[],
  tools?: Tool[]
): ReferenceAggregation[] {
  // Build a map of toolId -> reference if tools provided
  const toolReferenceMap = new Map<string, string>();
  if (tools) {
    tools.forEach((tool) => {
      if (tool.reference) {
        toolReferenceMap.set(tool.toolId, tool.reference);
      }
    });
  }

  // Group traces by reference
  const grouped = traces.reduce(
    (acc, trace) => {
      // Get reference from trace or from tool map
      const reference = trace.reference || toolReferenceMap.get(trace.toolId);

      // Skip traces without reference
      if (!reference) return acc;

      if (!acc[reference]) {
        acc[reference] = {
          reference,
          // Initialize sums
          totalMachiningDuration: 0,
          pieceMachiningDuration: 0,
          machinedVolume: 0,
          machinedDistance: 0,
          machinedPieces: 0,
          // Initialize for mean calculation
          malroundSum: 0,
          malroundCount: 0,
          // Track unique tools
          toolIds: new Set<string>(),
          traceCount: 0,
          // Keep first occurrence
          machine: trace.machine?.name ?? undefined,
          material: trace.material?.name,
          lubType: trace.lubType ?? undefined,
          holderType: trace.holderType ?? undefined,
        };
      }

      // Track unique tools
      acc[reference].toolIds.add(trace.toolId);
      acc[reference].traceCount += 1;

      // Sum metrics (treating null as 0)
      acc[reference].totalMachiningDuration += trace.totalMachiningDuration || 0;
      acc[reference].pieceMachiningDuration += trace.pieceMachiningDuration || 0;
      acc[reference].machinedVolume += trace.machinedVolume || 0;
      acc[reference].machinedDistance += trace.machinedDistance || 0;
      acc[reference].machinedPieces += trace.machinedPieces || 0;

      // Track malround for mean calculation (only non-null values)
      if (trace.malround !== null && trace.malround !== undefined) {
        acc[reference].malroundSum += trace.malround;
        acc[reference].malroundCount += 1;
      }

      return acc;
    },
    {} as Record<
      string,
      {
        reference: string;
        totalMachiningDuration: number;
        pieceMachiningDuration: number;
        machinedVolume: number;
        machinedDistance: number;
        machinedPieces: number;
        malroundSum: number;
        malroundCount: number;
        toolIds: Set<string>;
        traceCount: number;
        machine?: string;
        material?: string;
        lubType?: string;
        holderType?: string;
      }
    >
  );

  // Convert to array of aggregated references
  return Object.values(grouped).map((group) => ({
    reference: group.reference,
    // Summed metrics
    totalMachiningDuration: group.totalMachiningDuration,
    pieceMachiningDuration: group.pieceMachiningDuration,
    machinedVolume: group.machinedVolume,
    machinedDistance: group.machinedDistance,
    machinedPieces: group.machinedPieces,
    // Mean metric (or 0 if no valid malround values)
    malround: group.malroundCount > 0 ? group.malroundSum / group.malroundCount : 0,
    // Additional info
    toolCount: group.toolIds.size,
    traceCount: group.traceCount,
    // Categorical fields
    machine: group.machine,
    material: group.material,
    lubType: group.lubType,
    holderType: group.holderType,
  }));
}
import type { Trace } from '@/features/traceability/type/Tool';

/**
 * Sums up the machined pieces from an array of traces.
 * @param traces The array of traces to aggregate.
 * @returns The total number of machined pieces.
 */
export const aggregatePieces = (traces: Trace[]): number =>
  traces.reduce((acc, trace) => acc + (trace.machinedPieces ?? 0), 0);

/**
 * Aggregated data for a single tool
 */
export type ToolAggregation = {
  toolId: string;
  // Summed metrics
  totalMachiningDuration: number;
  pieceMachiningDuration: number;
  machinedVolume: number;
  machinedDistance: number;
  machinedPieces: number;
  // Mean metrics
  malround: number;
  // Additional info
  traceCount: number;
  // Raw traces for re-aggregation with user-selected aggregation fn
  rawTraces: Trace[];
  // Keep first occurrence of categorical fields for compatibility
  machine: Trace['machine'];
  material: string | undefined;
  lubType: string;
  lubBrand: string | null;
  holderType: string | null;
  holderBrand: string | null;
  clampingType: string | null;
  clampingBrand: string | null;
  toolState: Trace['toolState'];
  operator: string | null;
};

/**
 * Aggregates trace data by tool ID
 *
 * This function groups all traces for each tool and aggregates their metrics:
 * - SUM: totalMachiningDuration, pieceMachiningDuration, machinedVolume, machinedDistance, machinedPieces
 * - MEAN: malround (only non-null values)
 * - KEEP FIRST: machine, material, lubrication, holder, clamping, toolState, operator
 *
 * This is used to display one data point per tool in charts instead of one per trace.
 *
 * @param traces - Array of traces with toolId
 * @returns Array of aggregated tool data
 */
export function aggregateTracesByTool(
  traces: (Trace & { toolId: string })[]
): (ToolAggregation & { toolId: string })[] {
  // Group traces by toolId
  const grouped = traces.reduce(
    (acc, trace) => {
      const toolId = trace.toolId;

      if (!acc[toolId]) {
        acc[toolId] = {
          toolId,
          traces: [],
          // Initialize sums
          totalMachiningDuration: 0,
          pieceMachiningDuration: 0,
          machinedVolume: 0,
          machinedDistance: 0,
          machinedPieces: 0,
          // Initialize for mean calculation
          malroundSum: 0,
          malroundCount: 0,
          // Keep first occurrence
          firstTrace: trace,
        };
      }

      acc[toolId].traces.push(trace);

      // Sum metrics (treating null as 0)
      acc[toolId].totalMachiningDuration += trace.totalMachiningDuration || 0;
      acc[toolId].pieceMachiningDuration += trace.pieceMachiningDuration || 0;
      acc[toolId].machinedVolume += trace.machinedVolume || 0;
      acc[toolId].machinedDistance += trace.machinedDistance || 0;
      acc[toolId].machinedPieces += trace.machinedPieces || 0;

      // Track malround for mean calculation (only non-null values)
      if (trace.malround !== null) {
        acc[toolId].malroundSum += trace.malround;
        acc[toolId].malroundCount += 1;
      }

      return acc;
    },
    {} as Record<
      string,
      {
        toolId: string;
        traces: (Trace & { toolId: string })[];
        totalMachiningDuration: number;
        pieceMachiningDuration: number;
        machinedVolume: number;
        machinedDistance: number;
        machinedPieces: number;
        malroundSum: number;
        malroundCount: number;
        firstTrace: Trace & { toolId: string };
      }
    >
  );

  // Convert to array of aggregated tools
  return Object.values(grouped).map((group) => ({
    toolId: group.toolId,
    // Summed metrics
    totalMachiningDuration: group.totalMachiningDuration,
    pieceMachiningDuration: group.pieceMachiningDuration,
    machinedVolume: group.machinedVolume,
    machinedDistance: group.machinedDistance,
    machinedPieces: group.machinedPieces,
    // Mean metric (or 0 if no valid malround values)
    malround: group.malroundCount > 0 ? group.malroundSum / group.malroundCount : 0,
    // Additional info
    traceCount: group.traces.length,
    // Raw traces for re-aggregation with user-selected aggregation fn
    rawTraces: group.traces,
    // Keep first occurrence for categorical fields
    machine: group.firstTrace.machine,
    material: group.firstTrace.material?.name,
    lubType: group.firstTrace.lubType,
    lubBrand: group.firstTrace.lubBrand,
    holderType: group.firstTrace.holderType,
    holderBrand: group.firstTrace.holderBrand,
    clampingType: group.firstTrace.clampingType,
    clampingBrand: group.firstTrace.clampingBrand,
    toolState: group.firstTrace.toolState,
    operator: group.firstTrace.operator,
  }));
}

/**
 * Aggregation result for categorical dimensions
 */
export type CategoryAggregation = {
  category: string; // The unique value (machine name, material name, etc.)
  // Summed metrics
  totalMachiningDuration: number;
  pieceMachiningDuration: number;
  machinedVolume: number;
  machinedDistance: number;
  machinedPieces: number;
  // Mean metrics
  malround: number;
  // Additional info
  traceCount: number;
  // Raw traces for re-aggregation with user-selected aggregation fn
  rawTraces: Trace[];
};

/**
 * Aggregates trace data by a categorical dimension (machine, material, toolHolder, lubrication)
 *
 * This function groups all traces by a specific categorical field and aggregates their metrics:
 * - SUM: totalMachiningDuration, pieceMachiningDuration, machinedVolume, machinedDistance, machinedPieces
 * - MEAN: malround (only non-null values)
 *
 * @param traces - Array of traces
 * @param dimension - The categorical dimension to group by
 * @returns Array of aggregated category data
 */
export function aggregateTracesByCategory(
  traces: Trace[],
  dimension: 'machine' | 'material' | 'toolHolder' | 'lubrication'
): CategoryAggregation[] {
  // Helper to get the category value for a trace
  const getCategoryValue = (trace: Trace): string | null => {
    switch (dimension) {
      case 'machine':
        return trace.machine?.name || null;
      case 'material':
        return trace.material?.name ?? null;
      case 'toolHolder':
        return trace.holderType || null;
      case 'lubrication':
        return trace.lubType || null;
      default:
        return null;
    }
  };

  // Group traces by category value
  const grouped = traces.reduce(
    (acc, trace) => {
      const categoryValue = getCategoryValue(trace);

      // Skip traces with null/undefined category values
      if (!categoryValue) return acc;

      if (!acc[categoryValue]) {
        acc[categoryValue] = {
          category: categoryValue,
          // Initialize sums
          totalMachiningDuration: 0,
          pieceMachiningDuration: 0,
          machinedVolume: 0,
          machinedDistance: 0,
          machinedPieces: 0,
          // Initialize for mean calculation
          malroundSum: 0,
          malroundCount: 0,
          traceCount: 0,
          // Accumulate raw traces for re-aggregation
          traces: [],
        };
      }

      acc[categoryValue].traces.push(trace);

      // Sum metrics (treating null as 0)
      acc[categoryValue].totalMachiningDuration += trace.totalMachiningDuration || 0;
      acc[categoryValue].pieceMachiningDuration += trace.pieceMachiningDuration || 0;
      acc[categoryValue].machinedVolume += trace.machinedVolume || 0;
      acc[categoryValue].machinedDistance += trace.machinedDistance || 0;
      acc[categoryValue].machinedPieces += trace.machinedPieces || 0;

      // Track malround for mean calculation (only non-null values)
      if (trace.malround !== null) {
        acc[categoryValue].malroundSum += trace.malround;
        acc[categoryValue].malroundCount += 1;
      }

      acc[categoryValue].traceCount += 1;

      return acc;
    },
    {} as Record<
      string,
      {
        category: string;
        totalMachiningDuration: number;
        pieceMachiningDuration: number;
        machinedVolume: number;
        machinedDistance: number;
        machinedPieces: number;
        malroundSum: number;
        malroundCount: number;
        traceCount: number;
        traces: Trace[];
      }
    >
  );

  // Convert to array of aggregated categories
  return Object.values(grouped).map((group) => ({
    category: group.category,
    // Summed metrics
    totalMachiningDuration: group.totalMachiningDuration,
    pieceMachiningDuration: group.pieceMachiningDuration,
    machinedVolume: group.machinedVolume,
    machinedDistance: group.machinedDistance,
    machinedPieces: group.machinedPieces,
    // Mean metric (or 0 if no valid malround values)
    malround: group.malroundCount > 0 ? group.malroundSum / group.malroundCount : 0,
    // Additional info
    traceCount: group.traceCount,
    // Raw traces for re-aggregation with user-selected aggregation fn
    rawTraces: group.traces,
  }));
}
